import { useEffect, useMemo, useState } from "react";
import { surveysService, type ListSurveysParams, type Survey } from "@/api/surveys.service";
import { surveyConditionsEqual, type SurveyConditionValues } from "./surveyConditions";

type ExistingSurveysQuery = {
  edition?: string;
  publicationId?: number;
  editionId?: string;
  newTaxonomyEnabled: boolean;
};

function isLegacySurvey(survey: Survey): boolean {
  return survey.editionId === null;
}

function legacyTaxonomyParam(newTaxonomyEnabled: boolean): Pick<ListSurveysParams, "taxonomy"> {
  return newTaxonomyEnabled ? { taxonomy: "legacy" } : {};
}

async function findSurveysForEdition(
  editionId: string,
  publicationId: number | undefined,
  newTaxonomyEnabled: boolean,
): Promise<Survey[]> {
  const [byEdition, byPublication] = await Promise.all([
    surveysService.list({ editionId, limit: 100 }),
    publicationId
      ? surveysService.list({
          publicationId,
          ...legacyTaxonomyParam(newTaxonomyEnabled),
          limit: 100,
        })
      : null,
  ]);
  const surveys = new Map<string, Survey>();
  const legacyByPublication = (byPublication?.surveys ?? []).filter(isLegacySurvey);
  for (const survey of [...byEdition.surveys, ...legacyByPublication]) {
    surveys.set(survey.id, survey);
  }
  return [...surveys.values()];
}

function findExistingSurveys({
  edition,
  publicationId,
  editionId,
  newTaxonomyEnabled,
}: ExistingSurveysQuery): Promise<Survey[]> | null {
  if (editionId) return findSurveysForEdition(editionId, publicationId, newTaxonomyEnabled);
  if (!edition || !publicationId) return null;
  return (
    surveysService
      .list({ publicationId, edition, ...legacyTaxonomyParam(newTaxonomyEnabled), limit: 100 })
      // The backend edition filter is a partial (ILIKE) match, so pin the
      // exact edition here before comparing conditions.
      .then(response =>
        response.surveys.filter(survey => survey.edition === edition && isLegacySurvey(survey)),
      )
  );
}

type UseDuplicateSurveyWarningParams = {
  enabled: boolean;
  /** Full edition string ("2026 Edition UK"); matched exactly. */
  edition?: string;
  publicationId?: number;
  editionId?: string;
  newTaxonomyEnabled: boolean;
  /** Effective conditions to compare (collapsed section = empty conditions). */
  conditions: SurveyConditionValues;
  /** The survey being edited, so it never warns about itself. */
  excludeSurveyId?: string;
};

/**
 * Advisory live duplicate check for the survey create/edit forms. Fetches the
 * existing surveys once per edition+publication pair (not per checkbox click)
 * and compares condition combinations client-side via surveyConditionsEqual.
 *
 * Returns the first conflicting survey, or undefined. Non-blocking by design:
 * lookup failures are swallowed (console.error only) — the server's 409 on
 * save remains the enforcement.
 */
export function useDuplicateSurveyWarning({
  enabled,
  edition,
  publicationId,
  editionId,
  newTaxonomyEnabled,
  conditions,
  excludeSurveyId,
}: UseDuplicateSurveyWarningParams): Survey | undefined {
  const [existingSurveys, setExistingSurveys] = useState<Survey[]>([]);

  useEffect(() => {
    const request = enabled
      ? findExistingSurveys({ edition, publicationId, editionId, newTaxonomyEnabled })
      : null;
    if (!request) {
      setExistingSurveys([]);
      return;
    }
    let cancelled = false;
    request
      .then(surveys => {
        if (!cancelled) setExistingSurveys(surveys);
      })
      .catch(error => {
        // Advisory-only lookup: swallow the failure (no warning, no toast) —
        // the server's 409 on save remains the enforcement.
        if (!cancelled) setExistingSurveys([]);
        console.error("Failed to load existing surveys for duplicate warning", {
          error,
          edition,
          publicationId,
          editionId,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, edition, publicationId, editionId, newTaxonomyEnabled]);

  return useMemo(
    () =>
      existingSurveys.find(
        survey => survey.id !== excludeSurveyId && surveyConditionsEqual(survey, conditions),
      ),
    [existingSurveys, conditions, excludeSurveyId],
  );
}
